// ═══════════════════════════════════════════════════════════════════════════
// useCountdown — second-precision countdown timer.
//
// Originally inlined inside EmailVerificationPage to drive the resend-email
// cooldown. Pulled out as a hook because the same pattern is going to
// surface again on the upcoming PIN-setup retry timer, the 2FA TOTP
// re-arm and any future rate-limited action.
//
// API:
//   const { secondsLeft, start, reset } = useCountdown();
//   start(60); // begin a 60-second countdown
//
// Behaviour:
//   - `start(n)` (re)starts the countdown from `n` seconds.
//   - `reset()` zeroes the counter immediately and clears the timer.
//   - The hook decrements `secondsLeft` once per second via setTimeout
//     chain (no setInterval) so a missed tick doesn't accumulate drift.
//   - Cleans up its own timer on unmount and on every re-arm.
// ═══════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

interface UseCountdownReturn {
  /** Seconds remaining. `0` when idle. */
  secondsLeft: number;
  /** Starts (or restarts) a countdown from `seconds`. */
  start: (seconds: number) => void;
  /** Aborts the current countdown and zeros the counter. */
  reset: () => void;
  /** Convenience flag — `true` while the timer is running. */
  active: boolean;
}

export function useCountdown(): UseCountdownReturn {
  const [secondsLeft, setSecondsLeft] = useState(0);

  const start = useCallback((seconds: number) => {
    setSecondsLeft(Math.max(0, Math.floor(seconds)));
  }, []);

  const reset = useCallback(() => {
    setSecondsLeft(0);
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    // setTimeout (not setInterval): one-shot timer that re-arms via the
    // state change. If the tab sleeps for 2s the next decrement is
    // scheduled fresh — no burst of catch-up ticks.
    const id = window.setTimeout(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearTimeout(id);
  }, [secondsLeft]);

  return {
    secondsLeft,
    start,
    reset,
    active: secondsLeft > 0,
  };
}
